/**
 * Filter Configuration System
 *
 * Defines filter metadata for each column including:
 * - Data type (string, number, boolean, select, multiselect, date)
 * - Available options for select/multiselect filters
 * - Custom validation and formatting
 * - Display labels and placeholders
 */

export const FILTER_TYPES = {
  TEXT: "text",
  NUMBER: "number",
  BOOLEAN: "boolean",
  SELECT: "select",
  MULTISELECT: "multiselect",
  DATE: "date",
  DATE_RANGE: "dateRange",
  RANGE: "range",
};

/**
 * Filter configuration for each column
 * Maps column key to filter metadata
 */
export const FILTER_CONFIG = {
  // String/Text filters
  serialNumber: {
    type: FILTER_TYPES.TEXT,
    label: "Serial Number",
    placeholder: "Enter serial number...",
    description: "Search by device serial number",
  },
  siteId: {
    type: FILTER_TYPES.TEXT,
    label: "Site ID",
    placeholder: "Enter site ID...",
    description: "Search by site identifier",
  },
  displayName: {
    type: FILTER_TYPES.TEXT,
    label: "Display Name",
    placeholder: "Enter display name...",
    description: "Search by device display name",
  },
  stationName: {
    type: FILTER_TYPES.TEXT,
    label: "Station Name",
    placeholder: "Enter station name...",
    description: "Search by station name",
  },
  vendorName: {
    type: FILTER_TYPES.TEXT,
    label: "Vendor Name",
    placeholder: "Enter vendor name...",
    description: "Search by vendor",
  },
  modelName: {
    type: FILTER_TYPES.TEXT,
    label: "Model Name",
    placeholder: "Enter model name...",
    description: "Search by model",
  },
  partNum: {
    type: FILTER_TYPES.TEXT,
    label: "Part Number",
    placeholder: "Enter part number...",
    description: "Search by part number",
  },

  // Select filters with predefined options
  country: {
    type: FILTER_TYPES.SELECT,
    label: "Country",
    placeholder: "Select country...",
    description: "Filter by country",
    options: [
      { value: "US", label: "🇺🇸 United States" },
      { value: "UK", label: "🇬🇧 United Kingdom" },
      { value: "DE", label: "🇩🇪 Germany" },
      { value: "FR", label: "🇫🇷 France" },
      { value: "IN", label: "🇮🇳 India" },
      { value: "AU", label: "🇦🇺 Australia" },
      { value: "CA", label: "🇨🇦 Canada" },
      { value: "JP", label: "🇯🇵 Japan" },
      { value: "CN", label: "🇨🇳 China" },
      { value: "BR", label: "🇧🇷 Brazil" },
      { value: "NL", label: "🇳🇱 Netherlands" },
      { value: "IT", label: "🇮🇹 Italy" },
      { value: "ES", label: "🇪🇸 Spain" },
      { value: "KR", label: "🇰🇷 South Korea" },
      { value: "MX", label: "🇲🇽 Mexico" },
      { value: "SE", label: "🇸🇪 Sweden" },
      { value: "NO", label: "🇳🇴 Norway" },
      { value: "DK", label: "🇩🇰 Denmark" },
      { value: "FI", label: "🇫🇮 Finland" },
      { value: "CH", label: "🇨🇭 Switzerland" },
      { value: "BE", label: "🇧🇪 Belgium" },
      { value: "AT", label: "🇦🇹 Austria" },
      { value: "PT", label: "🇵🇹 Portugal" },
      { value: "IE", label: "🇮🇪 Ireland" },
      { value: "NZ", label: "🇳🇿 New Zealand" },
      { value: "SG", label: "🇸🇬 Singapore" },
    ],
    isMulti: false,
  },

  protocol: {
    type: FILTER_TYPES.SELECT,
    label: "Protocol",
    placeholder: "Select protocol...",
    description: "Filter by communication protocol",
    options: [
      { value: "OCPP", label: "OCPP" },
      { value: "MODBUS", label: "Modbus" },
      { value: "MQTT", label: "MQTT" },
      { value: "REST", label: "REST" },
      { value: "PROPRIETARY", label: "Proprietary" },
    ],
    isMulti: false,
  },

  protocolVersion: {
    type: FILTER_TYPES.SELECT,
    label: "Protocol Version",
    placeholder: "Select version...",
    description: "Filter by protocol version",
    options: [
      { value: "1.6", label: "OCPP 1.6" },
      { value: "2.0", label: "OCPP 2.0" },
      { value: "2.0.1", label: "OCPP 2.0.1" },
    ],
    isMulti: false,
  },

  commissioningStatus: {
    type: FILTER_TYPES.SELECT,
    label: "Commissioning Status",
    placeholder: "Select status...",
    description: "Filter by commissioning status",
    options: [
      { value: "COMMISSIONED", label: "Commissioned" },
      { value: "PENDING", label: "Pending" },
      { value: "FAILED", label: "Failed" },
      { value: "IN_PROGRESS", label: "In Progress" },
    ],
    isMulti: false,
  },

  mode: {
    type: FILTER_TYPES.SELECT,
    label: "Mode",
    placeholder: "Select mode...",
    description: "Filter by operating mode",
    options: [
      { value: "AC", label: "AC" },
      { value: "DC", label: "DC" },
      { value: "DUAL", label: "Dual" },
    ],
    isMulti: false,
  },

  dlbSource: {
    type: FILTER_TYPES.SELECT,
    label: "DLB Source",
    placeholder: "Select DLB source...",
    description: "Filter by dynamic load balancing source",
    options: [
      { value: "NONE", label: "None" },
      { value: "SITE", label: "Site" },
      { value: "GRID", label: "Grid" },
      { value: "SOLAR", label: "Solar" },
      { value: "BATTERY", label: "Battery" },
    ],
    isMulti: false,
  },

  timezone: {
    type: FILTER_TYPES.SELECT,
    label: "Timezone",
    placeholder: "Select timezone...",
    description: "Filter by timezone",
    options: [
      { value: "UTC", label: "UTC" },
      { value: "EST", label: "Eastern Standard Time" },
      { value: "CST", label: "Central Standard Time" },
      { value: "MST", label: "Mountain Standard Time" },
      { value: "PST", label: "Pacific Standard Time" },
      { value: "GMT", label: "Greenwich Mean Time" },
      { value: "CET", label: "Central European Time" },
      { value: "IST", label: "Indian Standard Time" },
      { value: "JST", label: "Japan Standard Time" },
      { value: "AEST", label: "Australian Eastern Standard Time" },
    ],
    isMulti: false,
  },

  // Number filters
  phaseCount: {
    type: FILTER_TYPES.NUMBER,
    label: "Phase Count",
    placeholder: "Enter phase count...",
    description: "Filter by number of phases",
    min: 1,
    max: 3,
  },

  maxCurrent: {
    type: FILTER_TYPES.RANGE,
    label: "Max Current (A)",
    description: "Filter by maximum current range in amperes",
    min: 0,
    max: 1000,
    step: 1,
  },

  breakerCurrent: {
    type: FILTER_TYPES.RANGE,
    label: "Breaker Current (A)",
    description: "Filter by breaker current range in amperes",
    min: 0,
    max: 200,
    step: 1,
  },

  operatingVoltage: {
    type: FILTER_TYPES.SELECT,
    label: "Operating Voltage",
    placeholder: "Select voltage...",
    description: "Filter by operating voltage",
    options: [
      { value: 120, label: "120" },
      { value: 208, label: "208" },
      { value: 240, label: "240" },
      { value: 277, label: "277" },
      { value: 480, label: "480" },
      { value: 600, label: "600" },
    ],
    isMulti: false,
  },

  // Boolean filters
  isConnected: {
    type: FILTER_TYPES.BOOLEAN,
    label: "Connection Status",
    description: "Filter by connection status",
    options: [
      { value: true, label: "✓ Connected" },
      { value: false, label: "✗ Disconnected" },
    ],
  },

  isRetired: {
    type: FILTER_TYPES.BOOLEAN,
    label: "Retired Status",
    description: "Filter by retired status",
    options: [
      { value: true, label: "Retired" },
      { value: false, label: "Active" },
    ],
  },

  isFaulted: {
    type: FILTER_TYPES.BOOLEAN,
    label: "Fault Status",
    description: "Filter by fault status",
    options: [
      { value: true, label: "Faulted" },
      { value: false, label: "No Faults" },
    ],
  },

  dlbEnabled: {
    type: FILTER_TYPES.BOOLEAN,
    label: "DLB Enabled",
    description: "Filter by dynamic load balancing status",
    options: [
      { value: true, label: "Enabled" },
      { value: false, label: "Disabled" },
    ],
  },

  isOffPeakEligible: {
    type: FILTER_TYPES.BOOLEAN,
    label: "Off-Peak Eligible",
    description: "Filter by off-peak eligibility",
    options: [
      { value: true, label: "Eligible" },
      { value: false, label: "Not Eligible" },
    ],
  },

  isDefaultOffPeakEligible: {
    type: FILTER_TYPES.BOOLEAN,
    label: "Default Off-Peak Eligible",
    description: "Filter by default off-peak eligibility",
    options: [
      { value: true, label: "Eligible" },
      { value: false, label: "Not Eligible" },
    ],
  },

  isDeviceReady: {
    type: FILTER_TYPES.BOOLEAN,
    label: "Device Ready",
    description: "Filter by device readiness",
    options: [
      { value: true, label: "Ready" },
      { value: false, label: "Not Ready" },
    ],
  },

  isOffGrid: {
    type: FILTER_TYPES.BOOLEAN,
    label: "Off-Grid",
    description: "Filter by off-grid status",
    options: [
      { value: true, label: "Off-Grid" },
      { value: false, label: "Grid-Connected" },
    ],
  },

  hoControl: {
    type: FILTER_TYPES.BOOLEAN,
    label: "HO Control",
    description: "Filter by home owner control status",
    options: [
      { value: true, label: "Enabled" },
      { value: false, label: "Disabled" },
    ],
  },

  // SKU (can be select if we have predefined SKUs)
  sku: {
    type: FILTER_TYPES.TEXT,
    label: "SKU",
    placeholder: "Enter SKU...",
    description: "Filter by product SKU",
  },

  fwVersion: {
    type: FILTER_TYPES.TEXT,
    label: "Firmware Version",
    placeholder: "Enter firmware version...",
    description: "Filter by firmware version",
  },

  sessionState: {
    type: FILTER_TYPES.SELECT,
    label: "Session State",
    description: "Filter by charging session state",
    options: [
      { value: "STARTED", label: "Started" },
      { value: "TX_STARTED", label: "TX Started" },
      { value: "CREATED", label: "Created" },
      { value: "ENDED", label: "Ended" },
      { value: "STOPPED", label: "Stopped" },
    ],
  },

  lastReportedAt: {
    type: FILTER_TYPES.SELECT,
    label: "Last Reported At",
    description: "Filter by when device last reported",
    options: [
      { value: "today", label: "Today" },
      { value: "last_7_days", label: "Last 7 Days" },
      { value: "last_30_days", label: "Last 30 Days" },
      { value: "last_90_days", label: "Last 90 Days" },
      { value: "last_year", label: "Last Year" },
    ],
  },

  createdAt: {
    type: FILTER_TYPES.SELECT,
    label: "Created At",
    description: "Filter by when device was created",
    options: [
      { value: "today", label: "Today" },
      { value: "last_7_days", label: "Last 7 Days" },
      { value: "last_30_days", label: "Last 30 Days" },
      { value: "last_90_days", label: "Last 90 Days" },
      { value: "last_year", label: "Last Year" },
    ],
  },

  updatedAt: {
    type: FILTER_TYPES.SELECT,
    label: "Updated At",
    description: "Filter by when device was last updated",
    options: [
      { value: "today", label: "Today" },
      { value: "last_7_days", label: "Last 7 Days" },
      { value: "last_30_days", label: "Last 30 Days" },
      { value: "last_90_days", label: "Last 90 Days" },
      { value: "last_year", label: "Last Year" },
    ],
  },
};

/**
 * Get filter configuration for a column
 * @param {string} columnKey - The column key
 * @returns {object} Filter configuration or default text filter
 */
export function getFilterConfig(columnKey) {
  return (
    FILTER_CONFIG[columnKey] || {
      type: FILTER_TYPES.TEXT,
      label: columnKey,
      placeholder: `Enter ${columnKey}...`,
      description: `Filter by ${columnKey}`,
    }
  );
}

/**
 * Get all available filter options for a column
 * @param {string} columnKey - The column key
 * @returns {array} Array of option objects or null if not applicable
 */
export function getFilterOptions(columnKey) {
  const config = getFilterConfig(columnKey);
  return config.options || null;
}

/**
 * Validate filter value against column type
 * @param {string} columnKey - The column key
 * @param {any} value - The filter value to validate
 * @returns {boolean} True if valid, false otherwise
 */
export function validateFilterValue(columnKey, value) {
  const config = getFilterConfig(columnKey);

  if (value === null || value === undefined || value === "") {
    return true; // Empty values are valid (means no filter)
  }

  switch (config.type) {
    case FILTER_TYPES.NUMBER: {
      const num = Number(value);
      if (isNaN(num)) return false;
      if (config.min !== undefined && num < config.min) return false;
      if (config.max !== undefined && num > config.max) return false;
      return true;
    }

    case FILTER_TYPES.BOOLEAN:
      return typeof value === "boolean" || value === "true" || value === "false";

    case FILTER_TYPES.SELECT:
    case FILTER_TYPES.MULTISELECT: {
      if (!config.options) return true;
      const values = Array.isArray(value) ? value : [value];
      return values.every(v => {
        const found = config.options.some(opt => {
          // Direct match
          if (opt.value === v) {
            return true;
          }
          // Type conversion match: if option is number and value is string, compare as numbers
          if (typeof opt.value === "number" && typeof v === "string") {
            return opt.value === Number(v);
          }
          return false;
        });
        return found;
      });
    }

    case FILTER_TYPES.RANGE: {
      if (typeof value === "object" && value !== null) {
        const { min, max } = value;
        // Allow partial input (either min or max, or both)
        if (min === "" && max === "") {
          return true; // Both empty is valid (no filter)
        }

        // Validate min if provided
        if (min !== "" && min !== undefined) {
          const minNum = Number(min);
          if (isNaN(minNum)) return false;
          if (config.min !== undefined && minNum < config.min) return false;
          if (config.max !== undefined && minNum > config.max) return false;
        }

        // Validate max if provided
        if (max !== "" && max !== undefined) {
          const maxNum = Number(max);
          if (isNaN(maxNum)) return false;
          if (config.min !== undefined && maxNum < config.min) return false;
          if (config.max !== undefined && maxNum > config.max) return false;
        }

        // If both are provided, validate min <= max
        if (min !== "" && max !== "" && min !== undefined && max !== undefined) {
          const minNum = Number(min);
          const maxNum = Number(max);
          if (minNum > maxNum) return false;
        }

        return true;
      }
      return false;
    }

    case FILTER_TYPES.TEXT:
    case FILTER_TYPES.DATE:
    case FILTER_TYPES.DATE_RANGE:
    default:
      return true;
  }
}

/**
 * Convert filter value to proper type
 * @param {string} columnKey - The column key
 * @param {any} value - The raw filter value
 * @returns {any} Converted value
 */
export function convertFilterValue(columnKey, value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const config = getFilterConfig(columnKey);

  switch (config.type) {
    case FILTER_TYPES.NUMBER:
      return Number(value);

    case FILTER_TYPES.BOOLEAN:
      if (typeof value === "boolean") return value;
      if (value === "true" || value === "1" || value === "yes") return true;
      if (value === "false" || value === "0" || value === "no") return false;
      return null;

    case FILTER_TYPES.SELECT:
    case FILTER_TYPES.MULTISELECT:
      // For SELECT filters, check if the option values are numeric
      if (config.options && config.options.length > 0) {
        const firstOption = config.options[0];
        // If the first option's value is a number, convert the string to number
        if (typeof firstOption.value === "number") {
          return Number(value);
        }
      }
      return value;

    case FILTER_TYPES.RANGE:
      // Range filters are objects with min and max
      if (typeof value === "object" && value !== null) {
        const { min, max } = value;
        // Allow partial values (min only, max only, or both)
        return {
          min: min !== "" && min !== undefined ? Number(min) : "",
          max: max !== "" && max !== undefined ? Number(max) : "",
        };
      }
      return value;

    case FILTER_TYPES.TEXT:
    case FILTER_TYPES.DATE:
    case FILTER_TYPES.DATE_RANGE:
    default:
      return String(value);
  }
}

/**
 * Get display label for a filter value
 * @param {string} columnKey - The column key
 * @param {any} value - The filter value
 * @returns {string} Display label
 */
export function getFilterValueLabel(columnKey, value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const config = getFilterConfig(columnKey);

  if (config.options) {
    const option = config.options.find(opt => opt.value === value);
    return option ? option.label : String(value);
  }

  return String(value);
}
